import type { UserRevokedAction } from '@Application/Security/Authentications/Actions/userRevoked';
import type { StaffMembersFetchedAction } from '@Application/Facilities/StaffMembers/StaffMembers/Actions/staffMembersFetched';
import type { StaffMemberCreatedAction } from '@Application/Facilities/StaffMembers/StaffMembers/Actions/staffMemberCreated';
import type { StaffMemberDeletedAction } from '@Application/Facilities/StaffMembers/StaffMembers/Actions/staffMemberDeleted';
import type { StaffMemberUpdatedAction } from '@Application/Facilities/StaffMembers/StaffMembers/Actions/staffMemberUpdated';
import StaffMembersListView from '@UI/Store/StaffMembers/Views/StaffMembersListView';
import StaffMemberView from '@UI/Store/StaffMembers/Views/StaffMemberView';
import { createSliceReducer, registerStore } from '@verclam/synapse';

export type StaffMembersStore = {
	staffMembers: StaffMembersListView | null;
};

const initialState: StaffMembersStore = {
	staffMembers: null,
};

type Actions =
	| StaffMembersFetchedAction
	| StaffMemberCreatedAction
	| StaffMemberUpdatedAction
	| StaffMemberDeletedAction
	| UserRevokedAction;

const whenStaffMembersFetched = (
	store: StaffMembersStore,
	action: StaffMembersFetchedAction,
): StaffMembersStore => ({
	...store,
	staffMembers: new StaffMembersListView(
		action.payload.map((staffMember) => new StaffMemberView(staffMember)),
		action.organizationId,
	),
});

const whenStaffMemberCreated = (
	store: StaffMembersStore,
	action: StaffMemberCreatedAction,
): StaffMembersStore => {
	if (!store.staffMembers || store.staffMembers.organizationId !== action.organizationId) {
		return store;
	}

	return {
		...store,
		staffMembers: store.staffMembers.addStaffMember(new StaffMemberView(action.payload)),
	};
};

const whenStaffMemberUpdated = (
	store: StaffMembersStore,
	action: StaffMemberUpdatedAction,
): StaffMembersStore => {
	if (!store.staffMembers || store.staffMembers.organizationId !== action.organizationId) {
		return store;
	}

	return {
		...store,
		staffMembers: store.staffMembers.updateStaffMember(new StaffMemberView(action.payload)),
	};
};

const whenStaffMemberDeleted = (
	store: StaffMembersStore,
	action: StaffMemberDeletedAction,
): StaffMembersStore => {
	if (!store.staffMembers || store.staffMembers.organizationId !== action.organizationId) {
		return store;
	}

	return {
		...store,
		staffMembers: store.staffMembers.removeStaffMember(action.payload.staffMemberId),
	};
};

const whenUserRevoked = (): StaffMembersStore => initialState;

export default registerStore<'staffMembersStore', Actions>(
	'staffMembersStore',
	createSliceReducer({
		STAFF_MEMBERS_FETCHED: whenStaffMembersFetched,
		STAFF_MEMBER_CREATED: whenStaffMemberCreated,
		STAFF_MEMBER_UPDATED: whenStaffMemberUpdated,
		STAFF_MEMBER_DELETED: whenStaffMemberDeleted,
		USER_REVOKED: whenUserRevoked,
	}),
	initialState,
);
